/**
 * Single-flight + short-TTL cache for idempotent reference GETs.
 *
 * Several endpoints return data that is effectively static for the length of a
 * page view — the model catalogue, upload limits — but are fetched by each
 * component that needs them. In split view that means one fetch per panel, and
 * on a cold load a handful of identical requests race each other. None of them
 * are cheap: each is a round trip plus real work server-side.
 *
 * This collapses them. Callers that ask while a request is in the air get that
 * request's promise; callers that ask shortly after get the cached value. A
 * failure is NOT cached, so a transient error doesn't poison the next attempt.
 *
 * Deliberately NOT a general HTTP cache: it is opt-in per call site, so nothing
 * gets stale data by accident. Use it for reference data, never for anything
 * the user has just changed.
 */

import api from '@services/api';

interface Entry<T> {
  at: number;
  value: T;
}

const DEFAULT_TTL_MS = 30_000;

const cache = new Map<string, Entry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

/**
 * GET `url`, sharing the request and its result with concurrent/recent callers.
 *
 * @param url  request path — also the cache key, so query strings scope it
 * @param ttlMs how long a successful result stays usable
 */
export async function cachedGet<T = unknown>(url: string, ttlMs: number = DEFAULT_TTL_MS): Promise<T> {
  const hit = cache.get(url) as Entry<T> | undefined;
  if (hit && Date.now() - hit.at < ttlMs) return hit.value;

  const pending = inFlight.get(url) as Promise<T> | undefined;
  if (pending) return pending;

  const promise = api.get(url)
    .then(res => {
      cache.set(url, { at: Date.now(), value: res.data });
      return res.data as T;
    })
    .finally(() => {
      inFlight.delete(url);
    });

  inFlight.set(url, promise);
  return promise;
}

/**
 * The fresh cached value for `url`, without fetching — undefined when there
 * is none. Lets a component that mounts after the data is already here
 * render its first frame from it instead of a placeholder.
 */
export function peekCachedGet<T = unknown>(url: string, ttlMs: number = DEFAULT_TTL_MS): T | undefined {
  const hit = cache.get(url) as Entry<T> | undefined;
  return hit && Date.now() - hit.at < ttlMs ? hit.value : undefined;
}

/** Drop cached entries. Pass a url to invalidate one, omit it to clear all. */
export function invalidateCachedGet(url?: string): void {
  if (url) cache.delete(url);
  else cache.clear();
}
